import React, { useEffect } from 'react';
import BlogLayout from '../../components/BlogLayout';

export default function WhatRoofersThinkAI() {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <BlogLayout
            title="What Roofers Think AI Is (And What It Actually Does)"
            date="March 10, 2026"
            readTime="5 MIN"
            heroImage="/images/blog/what-roofers-think-ai-hero.png"
            description="Say 'AI' to a roofing company owner and watch their eyes go somewhere else. They're picturing a robot. Or HAL 9000. What they're not picturing: someone answering their customer texts at 11 PM while they sleep."
        >
            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                You say "AI" to a roofing company owner and watch what happens. Their eyes go somewhere else for a second. Not glazed exactly — more like they're doing quick math on how complicated this is about to get.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                They're picturing a robot. Honestly. Some HAL 9000 situation on their crew. Or maybe they're picturing their phone autocorrecting "shingles" to "singles" again — and multiplying that by infinity.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                What they're not picturing: someone answering their customer texts at 11 PM while they sleep.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's the gap we have to close.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What they think it is</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's the mental model most roofers walk in with: new software to learn. A subscription that charges them when they forget to cancel. Something that needs a "tech guy" to configure. A tool that talks to them but doesn't actually do anything.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                They've tried chatbots. The chatbot was useless. Or they watched a YouTube video where some guy in a ring light spent 45 minutes explaining how to "prompt" an AI — while they had 14 jobs to quote by Friday.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Fair enough. Most AI demos deserve the skepticism they get.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What it actually does</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                A RoofClaw agent doesn't require prompting. It's not a chatbot you talk to. It's closer to a staff member who works 24 hours, never misses a follow-up, and doesn't call in sick during hail season.
            </p>

            <p className="mb-8 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Here's what it handles on a normal Monday:
            </p>

            <div className="space-y-4 mb-12">
                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">06:47 AM</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        A homeowner texts about a leak inspection. Too early for your crew to be reachable. The agent replies, gets their address, confirms availability, drops the appointment in your calendar. By 7:15 AM you have a booked inspection you didn't know about yet.
                    </p>
                </div>

                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">11:03 AM</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        A lead from Friday never got a follow-up. The agent noticed. It sent one at 10 AM. The lead replied. You have a call scheduled for 2 PM.
                    </p>
                </div>

                <div className="bg-[#18181B] border border-[#7B61FF]/20 rounded-2xl p-6">
                    <p className="font-data text-xs text-[#7B61FF] tracking-wider mb-2">03:15 PM</p>
                    <p className="text-[#F0EFF4]/80 text-base leading-relaxed">
                        Your estimate email from two days ago got no response. The agent sent a one-line check-in. The homeowner apologized, said they meant to reply, confirmed they want to move forward.
                    </p>
                </div>
            </div>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                None of this required you to do anything. No new app. No prompting. No learning curve.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The fear underneath the skepticism</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Most roofers aren't afraid of technology. They're afraid of their time being wasted by technology.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Every piece of software they've tried sold them on the promise and delivered the setup manual. They've been burned enough times that "AI" now sounds like "three days of configuration and a YouTube tutorial."
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's a reasonable response to their actual experience.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                The thing RoofClaw does differently: the agent ships configured. It already knows your business hours, service area, and how you want to handle new leads. The "setup" is us doing the setup — not you.
            </p>

            <div className="bg-[#0A0A14] border border-[#10B981]/20 rounded-2xl p-8 my-10">
                <p className="font-data text-xs text-[#10B981] tracking-wider mb-4">THE NUMBER WORTH KNOWING</p>
                <p className="text-[#F0EFF4] text-xl font-bold mb-3">45-60 minutes a day</p>
                <p className="text-[#F0EFF4]/70 text-base leading-relaxed">
                    That's what the average roofing contractor spends on follow-up emails and missed call callbacks. Roughly 15-20 hours a month. At $150/hour opportunity cost, that's $2,250–$3,000 in time going to tasks a machine handles better.
                </p>
            </div>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">What it's not</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                Worth being direct here.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                It's not going to replace your foreman. It won't get on a roof, close the hard deals, handle an irate homeowner face-to-face, or make judgment calls mid-job. Those things still need you.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                What it does: handle the 80% of communication that doesn't require judgment. So you can spend your judgment on the 20% that does.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                It won't learn your business overnight. The first couple weeks are calibration — it's figuring out your rhythms. By week three, it's catching follow-ups you forgot you had.
            </p>

            <hr className="my-12 border-[#F0EFF4]/10" />

            <h2 className="text-3xl font-heading font-bold mb-6 text-white tracking-tight">The roofer who sleeps now</h2>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                One of our clients used to answer customer texts at midnight. Not because he wanted to — because that's when they send them.
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                His words: <span className="text-[#F0EFF4] italic">"I felt like if I didn't reply fast, I'd lose the job."</span>
            </p>

            <p className="mb-6 text-[#F0EFF4]/80 text-lg leading-relaxed">
                His agent answers them now. Same fast reply. Same professional tone. He reviewed the transcripts for a month before he fully trusted it. Now he doesn't look at them anymore.
            </p>

            <p className="mb-12 text-[#F0EFF4] text-xl font-drama italic text-center my-8">
                He sleeps. The jobs still come in.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                That's what AI actually is, when it works. Not a robot. Not a chatbot. Not a $200/month subscription to a thing you'll never learn.
            </p>

            <p className="mb-12 text-[#F0EFF4]/80 text-lg leading-relaxed">
                It's your business, running at full speed — while you're at your kid's game on Saturday.
            </p>

            <hr className="my-16 border-[#F0EFF4]/10" />

            <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-[2rem] p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-64 h-64 bg-[#7B61FF] opacity-10 blur-[100px] rounded-full mix-blend-screen pointer-events-none"></div>
                <h2 className="text-3xl font-heading font-bold mb-6">Want to see it for your operation?</h2>
                <p className="text-lg text-[#F0EFF4]/80 mb-6">
                    No pitch deck. Just a 20-minute call where we walk through exactly what a configured agent would handle in a week for a business like yours.
                </p>
                <a
                    href="https://roofclaw.com/#contact"
                    className="inline-flex items-center gap-2 bg-[#7B61FF] text-white font-bold px-8 py-4 rounded-full hover:bg-[#6B51EF] transition-colors"
                >
                    Book a 20-minute call →
                </a>
            </div>
        </BlogLayout>
    );
}
